import {isBefore} from 'date-fns';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {parseDate} from 'shared/web';

import {isTelephonyCallLive} from './isTelephonyCallLive';
import {TelephonyCall, TelephonyCallStatus} from './types';

/**
 * Position of a status in the call lifecycle; a state never moves to a lower position. Inbound
 * `ringing` shares the start with outbound `ringing_user`; `in_call` and `ended` serve both.
 */
const TELEPHONY_CALL_STATUS_RANK: Record<TelephonyCallStatus, number> = {
  ringing: 0,
  ringing_user: 0,
  waiting_for_confirmation: 1,
  connecting_customer: 2,
  in_call: 3,
  ended: 4,
};

/**
 * Decides which current-call state to keep when two sources disagree. Pushes, the optimistic
 * `ringing_user` seed after `POST /calls`, and GET responses all land in the same cache entry and
 * may arrive out of order, so the cache only ever moves forward:
 *
 * - same call: the state further along the lifecycle wins, so a slow GET cannot revive an ended call;
 * - different calls: a live call beats an ended one, so a late `null`/ended response cannot hide a
 *   call that started meanwhile; of two live calls the later started one wins (it is what
 *   `/calls/current` serves), so a slow read of an outbound call cannot cover an inbound call that
 *   rang meanwhile; otherwise the incoming state wins, so the outbound call returns once the
 *   inbound one ended;
 * - `null` incoming (HTTP 204) keeps a live current call, because that request predates the start.
 */
export const mergeTelephonyCallState = (
  current: TelephonyCall | null | undefined,
  incoming: TelephonyCall | null
): TelephonyCall | null => {
  if (isNil(current)) {
    return incoming;
  }

  if (isNil(incoming)) {
    return isTelephonyCallLive(current) ? current : null;
  }

  if (current.id !== incoming.id) {
    return match([isTelephonyCallLive(current), isTelephonyCallLive(incoming)])
      .with([true, false], () => current)
      .with([true, true], () =>
        isBefore(parseDate(incoming.startedAt), parseDate(current.startedAt)) ? current : incoming
      )
      .otherwise(() => incoming);
  }

  return TELEPHONY_CALL_STATUS_RANK[incoming.status] >= TELEPHONY_CALL_STATUS_RANK[current.status]
    ? incoming
    : current;
};
